import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { CowreMark, IconAnimals, IconChart, IconCheckCircle, IconClose, IconDroplet } from '../../../common/icons';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { formatDateOnly } from '../../../common/utils/date';
import { useCreateOpuSession } from '../contexts/opu';
import { readDraft, clearDraft } from '../utils/draft';
import { isEmptyRow, validateAspirations } from '../utils/validate';
import AspirationRows from '../forms/aspiration-rows';

import './styles/session-workspace.css';

const ROLE_LABEL = { lead_vet: 'Aspirador', assistant: 'Asistente', operator: 'Operario' };
const isInt = (v) => v !== '' && Number.isInteger(Number(v)) && Number(v) >= 0;
const emptyRow = (seq) => ({ key: `r${seq}`, donor: null, total: '', viables: '', notes: '' });
const today = () => new Date().toLocaleDateString('en-CA'); // 'YYYY-MM-DD' wall-clock

function Stat({ icon, label, value, accent }) {
    return (
        <div className={`opu-stat ${accent ? 'opu-stat--accent' : ''}`}>
            <span className="opu-stat__icon">{icon}</span>
            <span className="opu-stat__body">
                <span className="opu-stat__value">{value}</span>
                <span className="opu-stat__label">{label}</span>
            </span>
        </div>
    );
}

export default function OpuSessionWorkspace() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const draftKey = params.get('draft');

    const [draft] = useState(() => readDraft(draftKey)); // read localStorage once
    // Clear the handed-off draft after mount so abandoning the tab (close/back)
    // never leaks an orphan entry. In an effect (not the initializer) to stay
    // pure under StrictMode's double-invoke.
    useEffect(() => { if (draftKey) clearDraft(draftKey); }, [draftKey]);

    const seq = useRef(1);
    const [rows, setRows] = useState(() => [emptyRow(0)]);
    const [errors, setErrors] = useState({});
    const { mutateAsync, isPending } = useCreateOpuSession();
    const confirm = useConfirm();

    if (!draft) {
        return (
            <div className="opu-ws opu-ws--empty">
                <CowreMark size={48} id="ws-empty" />
                <h1>No hay una sesión en borrador</h1>
                <p>Volvé a iniciar la creación de la OPU.</p>
                <button type="button" className="opu-ws__primary" onClick={() => navigate('/admin/opu/new')}>
                    Iniciar nueva OPU
                </button>
            </div>
        );
    }

    const addRow = () => setRows(prev => [...prev, emptyRow(seq.current++)]);
    // Removing the only row resets it to a fresh empty row (new key remounts the
    // donor search) so a single donor-only row is never a dead end.
    const removeRow = (key) => setRows(prev => (prev.length === 1 ? [emptyRow(seq.current++)] : prev.filter(r => r.key !== key)));

    const clearRowError = (key, field) =>
        setErrors(prev => (prev[key] ? { ...prev, [key]: { ...prev[key], [field]: undefined } } : prev));

    const setDonor = (key, donor) => {
        setRows(prev => prev.map(r => (r.key === key ? { ...r, donor } : r)));
        clearRowError(key, 'donor');
    };

    const setField = (key, field, value) => {
        // Active guard: block writing viables greater than the row's total.
        if (field === 'viables' && value !== '') {
            const row = rows.find(r => r.key === key);
            if (row && row.total !== '' && Number.isFinite(Number(value)) && Number(value) > Number(row.total)) {
                toast.error('Los viables no pueden superar los óvocitos totales');
                return;
            }
        }
        setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)));
        clearRowError(key, field);
    };

    // live summary — only rows with valid integer numbers (viables ≤ total) count
    const filled = rows.filter(r => !isEmptyRow(r));
    const aspiradas = filled.filter(r => r.donor).length;
    const numeric = filled.filter(r => isInt(r.total) && isInt(r.viables) && Number(r.viables) <= Number(r.total));
    const sumTotal = numeric.reduce((a, r) => a + Number(r.total), 0);
    const sumViables = numeric.reduce((a, r) => a + Number(r.viables), 0);
    const rate = sumTotal ? Math.round((sumViables / sumTotal) * 100) : 0;

    const cancel = async () => {
        if (filled.length) {
            const ok = await confirm({
                title: 'Descartar sesión',
                message: '¿Descartar la sesión en progreso?',
                confirmLabel: 'Descartar',
                danger: true,
            });
            if (!ok) return;
        }
        navigate('/admin/opu');
    };

    const handleSave = async () => {
        const { rowErrors, valid, considered } = validateAspirations(rows);
        if (!valid) {
            setErrors(rowErrors);
            toast.error(considered.length ? 'Revisá las filas marcadas' : 'Agregá al menos una donante');
            return;
        }
        setErrors({});

        const payload = {
            farm_id: draft.farm.id,
            date: draft.date || today(), // explicit; computed at save time (not stale)
            staff: (draft.staff || []).map(s => ({ user_id: s.user.id, role: s.role })),
            aspirations: considered.map(r => ({
                donor_id: r.donor.id,
                total: Number(r.total),
                viables: Number(r.viables),
                ...(r.notes?.trim() ? { remarks: r.notes.trim() } : {}),
            })),
        };

        try {
            const created = await mutateAsync(payload);
            toast.success(`Sesión ${created?.code || ''} creada`.trim());
            navigate(created?.id ? `/admin/opu/${created.id}` : '/admin/opu');
        } catch (err) {
            if (err?.status === 409) toast.error('Una donante está repetida en la sesión');
            else if (err?.status === 422) toast.error(err?.message || 'Regla de negocio rechazada');
            else toast.error(err?.message || 'No se pudo crear la sesión');
        }
    };

    return (
        <div className="opu-ws">
            <header className="opu-ws__topbar">
                <div className="opu-ws__brand">
                    <span className="opu-ws__mark"><CowreMark size={30} id="ws-mark" /></span>
                    <span className="opu-ws__brand-text">COWRE</span>
                    <span className="opu-ws__divider" />
                    <span className="opu-ws__title">Nueva sesión OPU</span>
                </div>
                <button type="button" className="opu-ws__close" onClick={cancel} aria-label="Cerrar">
                    <IconClose />
                </button>
            </header>

            <div className="opu-ws__content">
                <section className="opu-ws__summary">
                    <div className="opu-ws__summary-item">
                        <span className="opu-ws__summary-label">Finca</span>
                        <span className="opu-ws__summary-value">
                            {draft.farm.name}{draft.farm.owner?.name ? ` · ${draft.farm.owner.name}` : ''}
                        </span>
                    </div>
                    <div className="opu-ws__summary-item">
                        <span className="opu-ws__summary-label">Fecha</span>
                        <span className="opu-ws__summary-value">{draft.date ? formatDateOnly(draft.date) : 'Hoy'}</span>
                    </div>
                    <div className="opu-ws__summary-item">
                        <span className="opu-ws__summary-label">Equipo</span>
                        <span className="opu-ws__summary-value">
                            {draft.staff?.length
                                ? draft.staff.map(s => `${s.user.name} (${ROLE_LABEL[s.role] || s.role})`).join(', ')
                                : '—'}
                        </span>
                    </div>
                </section>

                <section className="opu-stats" aria-label="Resumen de la sesión">
                    <Stat icon={<IconAnimals />} label="Vacas aspiradas" value={aspiradas} />
                    <Stat icon={<IconDroplet />} label="Óvocitos totales" value={sumTotal} />
                    <Stat icon={<IconCheckCircle />} label="Viables" value={sumViables} />
                    <Stat icon={<IconChart />} label="Tasa de viabilidad" value={`${rate}%`} accent />
                </section>

                <AspirationRows
                    rows={rows}
                    errors={errors}
                    onAdd={addRow}
                    onField={setField}
                    onDonor={setDonor}
                    onClearDonor={(key) => setDonor(key, null)}
                    onRemove={removeRow}
                    disabled={isPending}
                />
            </div>

            <footer className="opu-ws__footer">
                <span className="opu-ws__footer-hint">{aspiradas} donante(s) · {sumViables}/{sumTotal} viables</span>
                <div className="opu-ws__actions">
                    <button type="button" className="opu-ws__cancel" onClick={cancel} disabled={isPending}>Cancelar</button>
                    <button type="button" className="opu-ws__primary" onClick={handleSave} disabled={isPending}>
                        {isPending ? 'Guardando…' : 'Guardar sesión'}
                    </button>
                </div>
            </footer>
        </div>
    );
}
